<template>
  <div ref="rootEl" class="sf-menu home__categories" :class="[`sf-menu--${template}`, `sf-menu--layout-${layout}`]">
    <h4 v-if="section.settings.title" class="sf-heading sf-category-title sf-menu__title">{{ section.settings.title }}</h4>

    <template v-if="visibleCategories.length">
      <!-- Por categorias: cards que abrem a página da categoria -->
      <div v-if="layout === 'categories'" class="sf-menu__cards">
        <RouterLink
          v-for="(category, index) in visibleCategories"
          :key="category.id"
          :to="{ name: 'categories.show', params: { id: category.id } }"
          class="sf-menu__card group"
        >
          <div class="sf-menu__card-media">
            <img
              v-if="getCategoryImage(category)"
              :src="getCategoryImage(category) as string"
              :alt="category.name"
              loading="lazy"
              class="transition-transform duration-300 group-hover:scale-105"
            >
            <div v-else class="sf-menu__card-initial" :class="{ 'is-alt': index % 2 === 1 }">{{ initialOf(category.name) }}</div>
          </div>
          <div class="sf-menu__card-body">
            <p class="sf-menu__card-name only-2-lines">{{ category.name }}</p>
            <p class="sf-menu__card-count">{{ countLabel(category) }}</p>
          </div>
        </RouterLink>
      </div>

      <template v-else>
        <div ref="anchorEl" class="sf-menu__anchor" aria-hidden="true"></div>
        <CategoryNavBar
          v-if="showNav"
          ref="navBar"
          :items="visibleCategories"
          :active-id="layout === 'tabs' ? selectedId : spy.activeId.value"
          :variant="layout === 'sections' ? 'chips' : 'underline'"
          :sticky="navSticky"
          :top="stickyTop"
          @select="onNavSelect"
        />

        <!-- Abas: só a categoria selecionada -->
        <div v-if="layout === 'tabs'" class="flex flex-col">
          <div v-if="selectedCategory" :key="selectedCategory.id" class="pb-10 home__categories__item sf-menu__tab-panel" role="tabpanel">
            <StoreProductGrid :products="selectedCategory.products" @select="(p: any) => emit('open-product', p)" />
          </div>
        </div>

        <!-- Página única / Delivery -->
        <div v-else class="flex flex-col">
          <div v-for="category in visibleCategories" :key="category.id" class="pb-10 home__categories__item" :data-sf-category="String(category.id)">
            <h5 class="sf-heading sf-category-title sf-menu__category-title">
              {{ category.name }}
              <span v-if="layout === 'delivery'" class="sf-menu__count">{{ countLabel(category) }}</span>
            </h5>
            <StoreProductGrid
              :products="category.products"
              :variant="layout === 'delivery' ? deliveryCardVariant : undefined"
              @select="(p: any) => emit('open-product', p)"
            />
          </div>
        </div>
      </template>
    </template>

    <div class="flex flex-col justify-center items-center sm:pt-14 m-auto" style="padding-bottom:7rem" v-if="!categories?.some((c: any) => c.products?.length) && restaurant?.id">
      <svg xmlns="http://www.w3.org/2000/svg" width="252" height="166" viewBox="0 0 252 166" fill="none">
        <path d="M218.84 48.2183V58.5663C238.937 45.3962 235.303 43.5147 250.611 62.543C252.92 65.4507 252.193 69.5984 249.071 71.4798L218.84 90.0377V145.54C218.84 156.401 209.689 165.082 198.828 165.082H53.1446C42.2835 165.082 33.1328 156.401 33.1328 145.54V90.0377L2.90145 71.4798C-0.220032 69.5984 -0.904194 65.3651 1.40485 62.543C16.713 43.9851 12.0949 44.7975 33.1328 58.5663C33.1328 50.5701 31.7218 44.541 37.3661 42.4885C57.164 34.193 77.2185 28.8053 97.5295 26.2824C91.3293 6.1424 124.853 -6.98494 145.592 6.27069C152.989 10.9743 157.009 18.6711 154.358 26.6245C174.284 29.3184 194.381 34.5779 214.607 42.4885C217.044 43.2154 218.84 45.5245 218.84 48.2183ZM110.315 25.0851L141.444 25.2989C149.611 16.7469 125.965 7.93833 112.838 16.3193C108.904 18.7994 107.493 21.9209 110.315 25.0851ZM55.1971 46.5935H194.595C148.842 36.2027 100.95 35.7324 55.1971 46.5935Z" fill="#E0E0E0"/>
      </svg>
      <h5 class="font-medium text-lg pt-2">Nenhum produto disponível</h5>
      <p class="sf-menu__muted text-center">Volte mais tarde</p>
    </div>

    <div v-if="!categories">
      <h5 class="mb-6 h-6 w-30 animate-pulse sf-skeleton"></h5>
      <div class="grid grid-cols-12 gap-4 md:gap-8 animate-pulse">
        <div class="col-span-6 md:col-span-4 lg:col-span-3 cursor-pointer" v-for="item in [1,2,3,4]" :key="item">
          <div class="w-full sf-radius h-40 sf-skeleton"></div>
          <h5 class="font-bold my-2 sf-skeleton w-full"><span class="opacity-0">skeleton</span></h5>
          <p class="font-bold sf-skeleton w-15"><span class="opacity-0">skeleton</span></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { restaurant, categories } from '@/global/state/GlobalState'
import { MenuSection, ProductCardVariant, storefront, storefrontTemplate, storefrontLayout } from '@/global/state/StorefrontState'
import StoreProductGrid from '../StoreProductGrid.vue'
import CategoryNavBar from '../CategoryNavBar.vue'
import { getCategoryImage } from '../storefrontUtils'
import { scrollToElement, useScrollSpy, useStickyTop } from '../useCategoryNav'

/* global defineProps, defineEmits */
defineProps<{ section: MenuSection }>()
const emit = defineEmits(['open-product'])

const template = storefrontTemplate
const layout = storefrontLayout

const rootEl = ref<HTMLElement | null>(null)
const anchorEl = ref<HTMLElement | null>(null)
const navBar = ref<any>(null)

const visibleCategories = computed<any[]>(() => (categories.value || []).filter((c: any) => c?.products?.length))

/** sections: chips only when the sticky bar is enabled; delivery: always (and always sticky); tabs: always. */
const showNav = computed(() => {
  if (layout.value === 'delivery' || layout.value === 'tabs') return true
  return layout.value === 'sections' && storefront.value.settings.stickyCategoryNav && visibleCategories.value.length > 1
})
const navSticky = computed(() => layout.value === 'delivery' || storefront.value.settings.stickyCategoryNav)
const spyEnabled = computed(() => showNav.value && (layout.value === 'sections' || layout.value === 'delivery'))

/** Delivery: product rows, or 2-column cards when columnsMobile is '2' (keeps the dark card on the dark template). */
const deliveryCardVariant = computed<ProductCardVariant>(() => {
  if (storefront.value.settings.columnsMobile !== '2') return 'classic'
  return template.value === 'dark' ? 'dark' : 'grid'
})

const stickyTop = useStickyTop()
const navHeight = () => (navBar.value?.$el as HTMLElement | undefined)?.offsetHeight ?? 0
const spy = useScrollSpy(rootEl, () => stickyTop.value + navHeight() + 12)

function refreshSpy() {
  nextTick(() => {
    if (spyEnabled.value) spy.observe()
    else spy.disconnect()
  })
}
onMounted(refreshSpy)
watch([visibleCategories, spyEnabled, layout, stickyTop], refreshSpy)

// Abas: primeira categoria por padrão; mantém a seleção quando os produtos recarregam
const selectedId = ref<string | null>(null)
watch(visibleCategories, (list) => {
  if (!list.some((c: any) => String(c.id) === selectedId.value)) selectedId.value = list.length ? String(list[0].id) : null
}, { immediate: true })
const selectedCategory = computed(() => visibleCategories.value.find((c: any) => String(c.id) === selectedId.value) ?? null)

function onNavSelect(id: string) {
  if (layout.value === 'tabs') {
    selectedId.value = id
    // bar stuck (page scrolled past the start of the menu): bring the top of the panel back into view
    const anchor = anchorEl.value
    if (anchor && navSticky.value && anchor.getBoundingClientRect().top < stickyTop.value) scrollToElement(anchor)
    return
  }
  const block = rootEl.value?.querySelector<HTMLElement>(`[data-sf-category="${CSS.escape(id)}"]`)
  if (!block) return
  spy.lock(id)
  scrollToElement(block, (navSticky.value ? navHeight() : 0) + 8)
}

const initialOf = (name?: string) => (name || '?').trim().charAt(0).toUpperCase()
const countLabel = (category: any) => {
  const n = category?.products?.length ?? 0
  return `${n} ${n === 1 ? 'item' : 'itens'}`
}
</script>

<style>
.sf-menu__title {
  font-size: 1.5rem;
  font-weight: 700;
  padding-bottom: 1.5rem;
}
.sf-menu__category-title {
  font-size: 1.25rem;
  font-weight: 700;
  padding-bottom: 1rem;
}
@media (min-width: 768px) {
  .sf-menu__title {
    font-size: 1.875rem;
  }
  .sf-menu__category-title {
    font-size: 1.5rem;
  }
}
.sf-menu--classic .sf-menu__category-title {
  padding-bottom: .25rem;
}
.sf-menu--showcase .home__categories__item {
  padding-bottom: 3.5rem;
}
.sf-menu--minimal {
  max-width: 64rem;
  margin: 0 auto;
}
.sf-menu--minimal .home__categories__item {
  padding-bottom: 3.5rem;
}
.sf-menu--minimal .sf-menu__category-title {
  padding-bottom: 1.5rem;
}
.sf-menu__muted {
  color: var(--sf-muted);
}
@media screen and (max-width: 1024px) {
  .home__categories {
    padding: 0 1rem;
  }
}

/* Delivery */
.sf-menu__count {
  margin-left: .5rem;
  font-family: var(--sf-font);
  font-size: .8125rem;
  font-weight: 500;
  color: var(--sf-muted);
}
.sf-menu--layout-delivery .sf-menu__category-title {
  font-size: 1.125rem;
  padding-bottom: .25rem;
}
@media (min-width: 768px) {
  .sf-menu--layout-delivery .sf-menu__category-title {
    font-size: 1.375rem;
  }
}

/* Abas */
.sf-menu__tab-panel {
  min-height: 40vh;
  animation: sf-menu-fade .25s ease;
}
@keyframes sf-menu-fade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

/* Por categorias */
.sf-menu__cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .875rem;
  padding-bottom: 3rem;
}
@media (min-width: 640px) {
  .sf-menu__cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
  }
}
@media (min-width: 1024px) {
  .sf-menu__cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.sf-menu__card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--sf-border);
  border-radius: var(--sf-radius);
  background: var(--sf-card-bg);
  color: var(--sf-text);
  transition: border-color .2s, box-shadow .2s;
}
.sf-menu__card:hover {
  border-color: var(--sf-primary);
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, .25);
}
.sf-menu__card-media {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: var(--sf-skeleton);
}
.sf-menu__card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sf-menu__card-initial {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--sf-heading-font);
  font-size: 2.75rem;
  font-weight: 700;
  background: var(--sf-primary);
  color: var(--sf-on-primary);
}
.sf-menu__card-initial.is-alt {
  background: var(--sf-secondary);
  color: var(--sf-on-secondary);
}
.sf-menu__card-body {
  padding: .75rem .875rem .875rem;
}
.sf-menu__card-name {
  font-family: var(--sf-heading-font);
  font-weight: 700;
  font-size: .9375rem;
  line-height: 1.3;
}
.sf-menu__card-count {
  margin-top: .2rem;
  font-size: .8125rem;
  color: var(--sf-muted);
}
</style>
